import type { SagaIterator } from 'redux-saga';
import { eventChannel } from 'redux-saga';
import { all, call, getContext, put, select, take } from 'redux-saga/effects';
import type { Socket } from 'socket.io-client';

import { SocketMessages } from '@app/sockets/socketTypes';
import bugsnag from '@app/utils/bugsnag';

import initAuthSagas from './features/auth/sagas';
import { saga as initProjectSaga } from './features/project';
import initProjectsSagas from './features/project/pages/ProjectsList/sagas';
import initSnapshotsSagas from './features/project/pages/Settings/pages/ProjectSnapshots/sagas';
import initAdvancedSecuritySagas from './features/team/pages/AdvancedSecurity/sagas';
import { getSessionBranchId, getSessionProjectId } from './selectors';
import { createSocketAction } from './sockets/socketActions';
import type { SocketMessage, SocketMessagesKeys } from './sockets/socketTypes';

function* createSocketChannel(socket: Socket): SagaIterator {
    const state = yield select();
    const projectId = getSessionProjectId(state);
    const branchId = getSessionBranchId(state);

    return eventChannel<SocketMessage>((emit) => {
        socket.on('connect', () => {
            socket.emit('global subscribe', {
                projectId,
                branchId,
            });
        });

        (Object.keys(SocketMessages) as SocketMessagesKeys[]).forEach((message) => {
            socket.on(SocketMessages[message], (payload) => emit({ message, payload }));
        });

        socket.on('error', (errorEvent: { reason: string }) => {
            bugsnag.log(`Socket.io error: ${errorEvent.reason}`);
        });

        return () => {
            (Object.keys(SocketMessages) as SocketMessagesKeys[]).forEach((message) => {
                socket.off(SocketMessages[message]);
            });
        };
    });
}

function* watchSocketConnection(): SagaIterator {
    const socketConnection = yield getContext('socketConnection');
    const socketChannel = yield call(createSocketChannel, socketConnection);

    while (true) {
        try {
            const event: SocketMessage = yield take(socketChannel);
            yield put(createSocketAction(event.message, event.payload));
        } catch (e) {
            bugsnag.log(`Socket.io error: ${e}`);
        }
    }
}

export default function* rootSaga(): SagaIterator {
    yield all([
        call(watchSocketConnection),
        call(initProjectsSagas),
        call(initSnapshotsSagas),
        call(initAdvancedSecuritySagas),
        call(initAuthSagas),
        call(initProjectSaga),
    ]);
}
